import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from "react";
import classNames from "classnames";
import * as Yup from "yup";
import { fetchLocation } from "@/apis";
import { LocationType } from "@/types";

export type AddressFormValues = {
    id?: string;
    name: string;
    phoneNumber: string;
    villageCity: string;
    houseNoStreet: string;
    tehsil: string;
    district: string;
    pincode: string;
    state: string;
    country: string;
    email?: string;
    isPrimary?: boolean;
};

export const createEmptyAddressValues = (): AddressFormValues => ({
    name: "",
    phoneNumber: "",
    villageCity: "",
    houseNoStreet: "",
    tehsil: "",
    district: "",
    pincode: "",
    state: "",
    country: "",
    email: "",
    isPrimary: false,
});

export const addressValidationSchema: Yup.ObjectSchema<AddressFormValues> = Yup.object().shape({
    name: Yup.string().trim().required("Name is required"),
    phoneNumber: Yup.string().trim(),
    email: Yup.string().trim().email("Invalid email").nullable(),
    villageCity: Yup.string().trim().required("Village / City is required"),
    houseNoStreet: Yup.string().trim().required("House number & street are required"),
    tehsil: Yup.string().trim().required("Tehsil is required"),
    district: Yup.string().trim().required("District is required"),
    pincode: Yup.string()
        .trim()
        .matches(/^[0-9]{6}$/, "Enter a valid 6 digit pincode")
        .required("Pincode is required"),
    state: Yup.string().trim().required("State is required"),
    country: Yup.string().trim().required("Country is required"),
    id: Yup.string().optional(),
    isPrimary: Yup.boolean().optional(),
});

type LocationOption = {
    id: string;
    name: string;
};


export type AddressErrors = Partial<Record<keyof AddressFormValues, string>>;

export type AddressFormProps = {
    values: AddressFormValues;
    onChange: (field: keyof AddressFormValues, value: string) => void;
    errors?: AddressErrors;
    disabled?: boolean;
    showContactFields?: boolean;
    className?: string;
    children?: ReactNode;
};

const AddressForm = ({
    values,
    onChange,
    errors = {},
    disabled = false,
    showContactFields = true,
    className = "",
    children,
}: AddressFormProps) => {
    const [countries, setCountries] = useState<LocationOption[]>([]);
    const [states, setStates] = useState<LocationOption[]>([]);
    const [districts, setDistricts] = useState<LocationOption[]>([]);
    const [tehsils, setTehsils] = useState<LocationOption[]>([]);

    const handleFetchLocations = useCallback(async (type: LocationType, query?: string) => {
        const { data } = await fetchLocation(type, query);
        return data?.response ?? [];
    }, []);

    useEffect(() => {
        let active = true;
        handleFetchLocations("COUNTRY")
            .then((data) => {
                if (active) setCountries(data);
            })
            .catch((error) => console.error("Failed to load countries", error));
        return () => {
            active = false;
        };
    }, [handleFetchLocations]);

    useEffect(() => {
        let active = true;
        if (!values.country) {
            setStates([]);
            setDistricts([]);
            setTehsils([]);
            return;
        }
        handleFetchLocations("STATE", values.country)
            .then((data) => {
                if (active) setStates(data);
            })
            .catch((error) => console.error("Failed to load states", error));
        return () => {
            active = false;
        };
    }, [values.country, handleFetchLocations]);

    useEffect(() => {
        let active = true;
        if (!values.state) {
            setDistricts([]);
            setTehsils([]);
            return;
        }
        handleFetchLocations("DISTRICT", values.state)
            .then((data) => {
                if (active) setDistricts(data);
            })
            .catch((error) => console.error("Failed to load districts", error));
        return () => {
            active = false;
        };
    }, [values.state, handleFetchLocations]);

    useEffect(() => {
        let active = true;
        if (!values.district) {
            setTehsils([]);
            return;
        }
        handleFetchLocations("TEHSIL", values.district)
            .then((data) => {
                if (active) setTehsils(data);
            })
            .catch((error) => console.error("Failed to load tehsils", error));
        return () => {
            active = false;
        };
    }, [values.district, handleFetchLocations]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        onChange(name as keyof AddressFormValues, value);
    };

    const containerClass = useMemo(
        () => classNames("row gy-3", className),
        [className]
    );

    const renderError = (field: keyof AddressFormValues) => {
        if (!errors[field]) return null;
        return <small className="text-danger">{errors[field]}</small>;
    };

    return (
        <>
            <div className={containerClass}>
                {showContactFields && (
                    <>
                        <div className="col-md-6">
                            <label htmlFor="address-name" className="form-label">
                                {`Name `}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                id="address-name"
                                name="name"
                                className="form-control"
                                placeholder="Full name"
                                value={values.name}
                                onChange={handleInputChange}
                                disabled={disabled}
                            />
                            {renderError("name")}
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="address-phone" className="form-label">
                                Phone
                            </label>
                            <input
                                type="text"
                                id="address-phone"
                                name="phoneNumber"
                                className="form-control"
                                placeholder="Phone number"
                                value={values.phoneNumber}
                                onChange={handleInputChange}
                                disabled={disabled}
                            />
                            {renderError("phoneNumber")}
                        </div>
                    </>
                )}
                <div className="col-md-6">
                    <label htmlFor="address-village" className="form-label">
                        Village / City <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        id="address-village"
                        name="villageCity"
                        className="form-control"
                        placeholder="Village or city"
                        value={values.villageCity}
                        onChange={handleInputChange}
                        disabled={disabled}
                    />
                    {renderError("villageCity")}
                </div>
                <div className="col-md-6">
                    <label htmlFor="address-house" className="form-label">
                        House No. & Street <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        id="address-house"
                        name="houseNoStreet"
                        className="form-control"
                        placeholder="House number and street"
                        value={values.houseNoStreet}
                        onChange={handleInputChange}
                        disabled={disabled}
                    />
                    {renderError("houseNoStreet")}
                </div>
                <div className="col-md-6">
                    <label htmlFor="address-country" className="form-label">
                        Country <span className="text-danger">*</span>
                    </label>
                    <select
                        id="address-country"
                        name="country"
                        className="form-select"
                        value={values.country}
                        onChange={handleInputChange}
                        disabled={disabled}
                    >
                        <option value="">Select country</option>
                        {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                    {renderError("country")}
                </div>
                <div className="col-md-6">
                    <label htmlFor="address-state" className="form-label">
                        State <span className="text-danger">*</span>
                    </label>
                    <select
                        id="address-state"
                        name="state"
                        className="form-select"
                        value={values.state}
                        onChange={handleInputChange}
                        disabled={disabled || !values.country}
                    >
                        <option value="">Select state</option>
                        {states.map((state) => (
                            <option key={state.id} value={state.id}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                    {renderError("state")}
                </div>
                <div className="col-md-6">
                    <label htmlFor="address-district" className="form-label">
                        District <span className="text-danger">*</span>
                    </label>
                    <select
                        id="address-district"
                        name="district"
                        className="form-select"
                        value={values.district}
                        onChange={handleInputChange}
                        disabled={disabled || !values.state}
                    >
                        <option value="">Select district</option>
                        {districts.map((district) => (
                            <option key={district.id} value={district.id}>
                                {district.name}
                            </option>
                        ))}
                    </select>
                    {renderError("district")}
                </div>
                <div className="col-md-6">
                    <label htmlFor="address-tehsil" className="form-label">
                        Tehsil <span className="text-danger">*</span>
                    </label>
                    <select
                        id="address-tehsil"
                        name="tehsil"
                        className="form-select"
                        value={values.tehsil}
                        onChange={handleInputChange}
                        disabled={disabled || !values.district}
                    >
                        <option value="">Select tehsil</option>
                        {tehsils.map((tehsil) => (
                            <option key={tehsil.id} value={tehsil.id}>
                                {tehsil.name}
                            </option>
                        ))}
                    </select>
                    {renderError("tehsil")}
                </div>
                <div className="col-md-6">
                    <label htmlFor="address-pincode" className="form-label">
                        Pincode <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        id="address-pincode"
                        name="pincode"
                        className="form-control"
                        placeholder="Pincode"
                        value={values.pincode}
                        onChange={handleInputChange}
                        disabled={disabled}
                    />
                    {renderError("pincode")}
                </div>
            </div>
            {children}
        </>
    );
};

export default AddressForm;
